import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Col, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, Row } from 'reactstrap';
import { InputField } from 'src/components';
import { config } from 'src/config';
import { setFromErrors } from 'src/helpers';
import { useUpdateLeaveEntryMutation } from 'src/rtk/features/leaveManagement/leaveManagementApi';
import { useGetLookupDataQuery } from 'src/rtk/features/setup/lookup/lookupDataApi';
import { useGetCurrentSlotInfoQuery } from 'src/rtk/features/trainingRecord/trainingRecordlApi';
import * as Yup from 'yup';
import loader from '../../assets/images/loader.gif';
import { useAppSelector } from 'src/rtk/app/hook';

const initialValues: ApplyForLeave = {
    leave_type: '',
    start_date: '',
    leave_document: '',
    end_date: '',
    no_of_days: '',
    leave_reason: '',
    submission_status: '',
};

interface EditProps {
    id?: string;
    setShowEditForm?: any;
    row?: any;
}

const ApplyForLeave = (props: EditProps) => {
    const { id, setShowEditForm, row } = props;
    const authUser = useAppSelector((state) => state.auth.user);

    console.log(row);
    const { data: currentInfo } = useGetCurrentSlotInfoQuery();

    const [updateLeaveEntry, { isSuccess, isError, error, isLoading }] = useUpdateLeaveEntryMutation();

    const [btnClicked, setBtnClicked] = useState<string>('');
    const [alertType, setAlertType] = useState<boolean>(false);

    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [daysDifference, setDaysDifference] = useState<any>('');
    const [photo, setPhoto] = useState('');

    const buttonRef = useRef<any>();
    const draftButtonRef = useRef<any>();

    const { data: leaveTypeLookupData } = useGetLookupDataQuery('leave_type');

    const typeList: Lookup[] = (leaveTypeLookupData as any)?.data[0]?.childs || [];

    const allLeaveType: SelectOption[] =
        typeList?.map((type) => ({
            value: type.name,
            label: type.name,
        })) || [];

    const leaveTypeForSupervisor: SelectOption[] = allLeaveType.filter(
        (type) => type.value !== 'Discontinue From Current Slot' && type.value !== 'Training Discontinue From Next Slot'
    );

    const err: CustomError = error as CustomError;

    const {
        handleSubmit,
        getFieldProps,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setValues,
        resetForm,
        setFieldValue,
        setErrors,
    } = useFormik({
        initialValues,

        validationSchema: Yup.object({
            leave_type: Yup.string().required('Please Select Type!'),
            start_date: Yup.date().required('Start Date is Required!'),
            end_date: Yup.date().when("start_date", (start_date, schema) => {
                if (start_date) {
                    const fromDate = new Date(start_date);

                    return schema.required('End date is Required').min(fromDate, 'To date must be greater than or equal start date').nullable();
                }

                return schema;
            }),
            no_of_days: Yup.string().required('No of Days is Required!'),
            leave_reason: Yup.string().required('Enter Leave Reason!'),
        }),

        onSubmit: (values: any) => {
            const formData = new FormData();

            Object.keys(values).forEach((key) => {
                if (key === 'submission_status') {
                    if (btnClicked === 'submit') {
                        values[key] = 'Submit';
                    } else if (btnClicked === 'save_as_draft') {
                        values[key] = 'Draft';
                    }
                }
                formData.append(key, values[key]);
            });
            console.log(values);
            updateLeaveEntry({ data: formData, id: row.id });
        },
    });

    useEffect(() => {
        if (isSuccess) {
            resetForm();
            toast.success('Leave Entry Updated Successfully!');
            setShowEditForm(false);
        }
        if (isError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err.data, setErrors);
        }
    }, [isSuccess, isError]);

    const handleStartDate = (e: any) => {
        setStartDate(e.target.value);
    };

    const handleEndDate = (e: any) => {
        setEndDate(e.target.value);
    };

    useEffect(() => {
        if (startDate !== '' && endDate !== '') {
            const from = new Date(startDate);
            const to = new Date(endDate);

            if (from <= to) {
                const differenceInTime = to.getTime() - from.getTime();
                const differenceInDays = differenceInTime / (1000 * 3600 * 24);
                setDaysDifference(Math.round(differenceInDays) + 1);
            } else {
                setDaysDifference('');
            }
        }

    }, [startDate, endDate]);

    useEffect(() => {
        if (daysDifference > 0) {
            setFieldValue('no_of_days', daysDifference);
        }
        else {
            setFieldValue('no_of_days', '');
        }
    }, [daysDifference]);

    useEffect(() => {
        setStartDate(row?.start_date);
        setEndDate(row?.end_date);
        if (row) {
            setPhoto(`${config?.APP_URL}${row?.leave_document}`);
            setValues({
                leave_type: row?.leave_type || '',
                start_date: row?.start_date || '',
                leave_document: '',
                end_date: row?.end_date || '',
                no_of_days: row?.no_of_days || '',
                leave_reason: row?.leave_reason || '',
                submission_status: row?.submission_status || '',
            });
        }
    }, [row]);

    useEffect(() => {
        if (values.leave_type === 'Discontinue From Current Slot') {
            setAlertType(true);
        } else {
            setAlertType(false);
        }
    }, [values.leave_type]);

    return (
        <React.Fragment>
            {isLoading ? (
                <div className="overlay">
                    <img src={loader} alt="Loading" height={100} width={300} />
                </div>
            ) : null}
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
                onReset={(e) => {
                    setShowEditForm(true);
                    resetForm();
                    setDaysDifference('');
                }}
            >
                <Row>
                    <Col sm={6}>
                        <FormGroup>
                            <Label for="start_date" className="form-label required-field">From Date</Label>
                            <InputField
                                type="date"
                                name='start_date'
                                onChange={(e: any) => {
                                    handleChange(e);
                                    handleStartDate(e);
                                }}
                                onBlur={handleBlur}
                                value={values.start_date}
                                invalid={{ errors, touched }}
                            />
                        </FormGroup>
                    </Col>
                    <Col sm={6}>
                        <FormGroup>
                            <Label for="end_date" className="form-label required-field">To Date</Label>
                            <InputField
                                type="date"
                                name='end_date'
                                onChange={(e: any) => {
                                    handleChange(e);
                                    handleEndDate(e);
                                }}
                                onBlur={handleBlur}
                                value={values.end_date}
                                invalid={{ errors, touched }}
                            />
                        </FormGroup>
                    </Col>

                    <Col sm={6}>
                        <FormGroup>
                            <Label for="no_of_days" className="form-label required-field">No. Of Days</Label>
                            <InputField
                                type="text"
                                placeholder='No. Of Days'
                                name='no_of_days'
                                value={daysDifference ? daysDifference : values.no_of_days}
                                disabled
                                invalid={{ errors, touched }}
                            />
                        </FormGroup>
                    </Col>

                    <Col sm={6}>
                        <FormGroup>
                            <Label for="leave_type" className="form-label required-field">Leave Type</Label>
                            <InputField
                                type="select"
                                options={authUser?.type === 'Supervisor' ? leaveTypeForSupervisor : allLeaveType}
                                name='leave_type'
                                value={values.leave_type}
                                onChange={handleChange}
                                invalid={{ errors, touched }}
                            />
                        </FormGroup>
                    </Col>

                    {(values.leave_type === 'Discontinue From Current Slot' || values.leave_type === 'Training Discontinue From Next Slot') ?
                        <Col sm={12}>
                            <FormGroup>
                                <Label for="leave_document" className="form-label required-field">Leave Application</Label>
                                <a
                                    className="me-2 p-0 center"
                                    href={photo}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <span className="btn-outline-info btn-sm btn ms-2">View</span>
                                </a>
                                <input
                                    name="leave_document"
                                    type="file"
                                    className="form-control"
                                    placeholder="Upload"
                                    onChange={(e: any) => {
                                        setFieldValue('leave_document', e.target.files[0]);
                                    }}
                                ></input>
                                {errors.leave_document && touched.leave_document ? (
                                    <div className="text-danger error-size">{errors.leave_document as string}</div>
                                ) : null}
                            </FormGroup>
                        </Col> : null
                    }

                    <Col sm={12}>
                        <FormGroup>
                            <Label for="leave_reason" className="form-label required-field">Reason</Label>
                            <InputField
                                type="textarea"
                                placeholder='Enter Reason'
                                {...getFieldProps('leave_reason')}
                                invalid={{ errors, touched }}
                            />
                        </FormGroup>
                    </Col>
                    <FormGroup className="mb-3 text-center">
                        <Button
                            type="reset"
                            className="btn btn-danger me-2"
                        >
                            Reset
                        </Button>
                        <button
                            type="submit"
                            className="btn btn-info me-2"
                            ref={draftButtonRef}
                            onClick={(e) => {
                                setBtnClicked('save_as_draft');
                            }}
                        >
                            Save as Draft
                        </button>
                        <button
                            type="submit"
                            ref={buttonRef}
                            className="btn btn-success"
                            onClick={(e) => {
                                setBtnClicked('submit');
                            }}
                        >
                            Update
                        </button>
                    </FormGroup>
                </Row>
            </Form>
            <Modal isOpen={alertType} toggle={() => setAlertType(!alertType)} backdrop="static" size='md' centered>
                <ModalBody className="text-center">
                    If you select Discontinue From Current Slot, it will remove you from your Current Slot
                    &nbsp;<strong>{currentInfo?.data?.exam_training_info?.training_type} - {currentInfo?.data?.exam_training_info?.name}</strong>
                    &nbsp;Session : <strong>{currentInfo?.data?.session_name}</strong> Year : <strong>{currentInfo?.data?.session_year}</strong>
                    . Are you sure you want to proceed?
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={() => setAlertType(!alertType)}>Yes, Continue</Button>
                    <Button
                        onClick={() => {
                            setFieldValue('leave_type', '');
                            setAlertType(!alertType);
                        }}
                    >
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default ApplyForLeave;
